// Pure math/calculation utilities for forecasting

// Exponential smoothing
export const calculateSmoothedForecast = (sku: string, month: string, alpha: number = 0.3, salesData: any[]) => {
  // Get all historical data points for this SKU and month, sorted by period
  const historicalData = salesData
    .filter(item => item.sku === sku && item.period.split('-')[1] === month.split('-')[1])
    .sort((a, b) => a.period.localeCompare(b.period));
  if (historicalData.length === 0) return 0;
  let smoothedValue = historicalData[0].salesQuantity;
  for (let i = 1; i < historicalData.length; i++) {
    smoothedValue = alpha * historicalData[i].salesQuantity + (1 - alpha) * smoothedValue;
  }
  return smoothedValue;
};

/**
 * Calculates the Year-over-Year (YOY) percentage change
 * @param current The current/forecast value
 * @param historical The historical average value
 * @returns The YOY percentage change
 */
export const calculateYOY = (current: number, historical: number): number => {
  if (historical === 0) return 0;
  return ((current - historical) / historical) * 100;
};

// Monthly averages
export const calculateMonthlyAverages = (salesData: any[]) => {
  const result: { [key: string]: { avgQty: number, avgRevenue: number } } = {};
  const skuGroups: { [sku: string]: any[] } = {};
  salesData.forEach(item => {
    if (!skuGroups[item.sku]) {
      skuGroups[item.sku] = [];
    }
    skuGroups[item.sku].push(item);
  });
  Object.entries(skuGroups).forEach(([sku, data]) => {
    const uniquePeriods = new Set(data.map(item => item.period)).size;
    const totalQty = data.reduce((sum, item) => sum + item.salesQuantity, 0);
    const totalRevenue = data.reduce((sum, item) => sum + item.salesRevenue, 0);
    result[sku] = {
      avgQty: totalQty / uniquePeriods,
      avgRevenue: totalRevenue / uniquePeriods
    };
  });
  return result;
};

// Historical averages by month
export const calculateHistoricalAverages = (salesData: any[]) => {
  const skuMonthData: { [key: string]: { [month: string]: { qty: number[], revenue: number[] } } } = {};
  salesData.forEach(item => {
    if (item.salesQuantity <= 0 && item.salesRevenue <= 0) return;
    const sku = item.sku;
    const month = item.period.split('-')[1];
    if (!skuMonthData[sku]) {
      skuMonthData[sku] = {};
    }
    if (!skuMonthData[sku][month]) {
      skuMonthData[sku][month] = { qty: [], revenue: [] };
    }
    skuMonthData[sku][month].qty.push(item.salesQuantity);
    skuMonthData[sku][month].revenue.push(item.salesRevenue);
  });
  const result: { [key: string]: { [month: string]: { avgQty: number, avgRevenue: number } } } = {};
  Object.entries(skuMonthData).forEach(([sku, monthData]) => {
    result[sku] = {};
    Object.entries(monthData).forEach(([month, data]) => {
      if (data.qty.length > 0) {
        const sumQty = data.qty.reduce((sum, qty) => sum + qty, 0);
        const sumRev = data.revenue.reduce((sum, rev) => sum + rev, 0);
        const len = data.qty.length;
        result[sku][month] = {
          avgQty: sumQty / len,
          avgRevenue: sumRev / len
        };
      }
    });
  });
  return result;
};

// Number formatting
export const formatNumberWithCommas = (num: number, decimals: number = 0): string => {
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

// Comparison text formatting
export const formatComparisonText = (diff: number, diffPercent: number): string => {
  const sign = diff > 0 ? '+' : '';
  return `${sign}${diffPercent.toFixed(0)}%`;
}; 